import {
    AttributeDefinitionType,
    CommonAnalyticsObject,
    ContainerType,
    NotificationPropsType,
    TaskAttribute,
    TaskConfigType,
} from 'Types/Tasks.types'
import {
    AttributeGroupConfig,
    AttributeGroupEvent,
    IAttributeGroup,
} from './AttributeGroup/BaseAttributeGroup'
import { AttributeGroupRegistry } from './AttributeGroupRegistry'
import { TaskState } from 'Core/TaskState'
import { Studio } from 'Core/Studio'
import { createReactElement, UpdatableReactElement } from 'shared/utils/react.utils'
import { Attributes } from 'Components/tasks/Attributes/Attributes'
import { ReviewIcon } from 'Components/common/Icons/ReviewIcon'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { DebouncedFunc } from 'lodash'

export type AttributeContext = {
    taskConfig: TaskConfigType<ContainerType, unknown>
    taskState: TaskState<ContainerType, unknown>
    studio: Studio
    saveTask: DebouncedFunc<
        (options?: { onComplete?: (props?: NotificationPropsType) => void }) => Promise<void>
    >
    attributesOnUI: { [key: string]: 1 }
    validateQCAttributes?: () => void
    getCommonAnalyticsObject: () => CommonAnalyticsObject
    task: CommonTask<ContainerType, unknown>
}

type AttributeGroupKey = string

export class AttributeManager {
    private attributeGroupRegistry = AttributeGroupRegistry.getInstance()
    private attributeContext: AttributeContext
    private attributeGroups: Map<AttributeGroupKey, IAttributeGroup> = new Map()
    private attributeCollectionUI?: UpdatableReactElement
    private dependencies: Map<AttributeDefinitionType, AttributeDefinitionType[]> = new Map()
    private isCollectionCreated = false

    constructor(attributeContext: AttributeContext) {
        this.attributeContext = attributeContext
    }

    public useAttributeGroup(attributeGroupConfig: {
        groupKey: AttributeGroupKey
        type: AttributeDefinitionType
        attributes: TaskAttribute[]
        config?: AttributeGroupConfig
    }) {
        const useInCollection = attributeGroupConfig.config?.useInCollection ?? false
        const isDisabled = attributeGroupConfig.config?.isDisabled ?? false
        const groupConstructor = this.attributeGroupRegistry.get(attributeGroupConfig.type)
        if (!groupConstructor) {
            throw new Error(`Group for type ${attributeGroupConfig.type} not found`)
        }
        const groupKey = attributeGroupConfig.groupKey
        const config = {
            groupKey,
            useInCollection,
            props: attributeGroupConfig.config?.props,
            taskProps: attributeGroupConfig.config?.taskProps,
            attributes: attributeGroupConfig.attributes,
        }

        const group = new groupConstructor(this.attributeContext, config)
        this.attributeGroups.set(groupKey, group)

        group.on(AttributeGroupEvent.Change, () => {
            this.renderDependentGroups(attributeGroupConfig.type)
        })

        if (
            !this.isCollectionCreated &&
            useInCollection &&
            !!attributeGroupConfig.attributes.length
        ) {
            this.createAndAddSidebarAttributeCollection()
        }

        if (isDisabled) {
            group.disable()
        }

        if (useInCollection) {
            this.attributeCollectionUI?.updater.forceUpdate()
        }
    }

    public defineDependencies(
        dependencies: { source: AttributeDefinitionType; dependents: AttributeDefinitionType[] }[]
    ) {
        dependencies.forEach(({ source, dependents }) => {
            this.dependencies.set(source, dependents)
        })
    }

    getAllAttributeGroups(): IAttributeGroup[] {
        return Array.from(this.attributeGroups.values())
    }

    getAttributeGroupsByDefinitionType(type: AttributeDefinitionType): IAttributeGroup[] {
        return Array.from(this.attributeGroups.values()).filter(
            group => group.attributeDefinitionType === type
        )
    }

    getAttributeGroup(groupKey: AttributeGroupKey): IAttributeGroup | undefined {
        const group = this.attributeGroups.get(groupKey)
        if (!group) {
            console.info(`Group for key ${groupKey} not found`)
            return
        }
        return group
    }

    private renderDependentGroups(sourceType: AttributeDefinitionType) {
        const dependents = this.dependencies.get(sourceType) || []
        dependents.forEach(dependentType => {
            const dependentGroups = this.getAttributeGroupsByDefinitionType(dependentType)
            if (dependentGroups.length) {
                dependentGroups.forEach(group => {
                    group.render()
                })
            }
        })
    }

    async validateGroups() {
        const isGroupsValid = await Promise.all(
            Array.from(this.attributeGroups.values()).map(group => group.validate())
        )
        return isGroupsValid.every(isValid => isValid)
    }

    async validateGroupsByDefinitionType(definitionType: AttributeDefinitionType) {
        const isGroupsValid = await Promise.all(
            this.getAttributeGroupsByDefinitionType(definitionType).map(group => group.validate())
        )
        return isGroupsValid.every(isValid => isValid)
    }

    private createAndAddSidebarAttributeCollection() {
        this.attributeCollectionUI = createReactElement(Attributes, {
            getGroupElements: this.getCollectionGroupsElements.bind(this),
        })
        this.attributeContext.studio.workbench.workbenchUiAccessor?.rightSidebar.addItem({
            id: 'review',
            element: this.attributeCollectionUI.element,
            title: 'Supplementary Details',
            icon: ReviewIcon,
        })
        this.isCollectionCreated = true
    }

    private getCollectionGroupsElements() {
        const groups = Array.from(this.attributeGroups.values()).filter(
            group => group.useInCollection
        )
        return (
            groups
                .map(group => group.getElement())
                .filter(element => element !== undefined)
                .map(element => element.element) ?? []
        )
    }

    removeAttributeGroup(groupKey: AttributeGroupKey) {
        const group = this.attributeGroups.get(groupKey)
        if (group) {
            group.destroy()
            this.attributeGroups.delete(groupKey)
        }
    }

    renderAllGroups() {
        for (const group of this.attributeGroups.values()) {
            group.render()
        }
    }
}
